import { useState } from "react";
import { Loader2, MailCheck } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useI18n } from "@/contexts/I18nContext";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onSignup: (email: string, password: string) => Promise<void>;
  onForgotPassword: (email: string) => Promise<void>;
}

export function LoginModal({ isOpen, onClose, onLogin, onSignup, onForgotPassword }: LoginModalProps) {
  const { t } = useI18n();
  const [mode, setMode] = useState<"login" | "signup" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupDone, setSignupDone] = useState(false);

  const handleClose = () => {
    setSignupDone(false);
    setError("");
    setSuccess("");
    setPassword("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "login") {
        await onLogin(email, password);
        handleClose();
      } else if (mode === "signup") {
        await onSignup(email, password);
        // If onSignup didn't throw and the user still isn't authenticated
        // (Supabase with email confirmation enabled), show the check-email panel.
        // Parent will close the modal automatically on auth state change if
        // the account is session-ready.
        setSignupDone(true);
      } else {
        await onForgotPassword(email);
        setSuccess(t("auth.resetLinkSent"));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.toLowerCase().includes("rate limit") || msg.toLowerCase().includes("too many")) {
        setError(t("auth.rateLimitError"));
      } else {
        setError(msg || t("auth.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={signupDone ? t("auth.signupSuccessTitle") : mode === "login" ? t("auth.loginTitle") : mode === "signup" ? t("auth.signupTitle") : t("auth.forgotTitle")}
      size="sm"
    >
      {signupDone ? (
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3 rounded-lg bg-green-500/10 border border-green-500/30 px-3 py-3">
            <MailCheck size={22} className="shrink-0 text-green-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white/90 truncate">{email}</p>
              <p className="text-xs text-white/60 mt-0.5">{t("auth.signupCheckEmail")}</p>
            </div>
          </div>
          <p className="text-xs text-white/40 leading-relaxed">{t("auth.signupSpamHint")}</p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full py-2.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white font-medium transition-colors"
          >
            {t("auth.signupClose")}
          </button>
        </div>
      ) : (
      <>
      {error && (
        <div className="mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 px-3 py-2 rounded-md bg-green-500/10 border border-green-500/30 text-green-200 text-sm">
          {success}
        </div>
      )}

      {mode === "forgot" && !success && (
        <p className="mb-4 text-sm text-white/60">{t("auth.forgotDesc")}</p>
      )}
      {mode === "signup" && (
        <p className="mb-4 text-sm text-white/50">{t("auth.passwordHint")}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-1.5 text-sm text-white/70">
            {t("auth.email")}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-[var(--app-fg,#fff)] focus:outline-none focus:border-violet-500 transition-colors"
          />
        </div>

        {mode !== "forgot" && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm text-white/70">{t("auth.password")}</label>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}
                  className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                >
                  {t("auth.forgotPassword")}
                </button>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-[var(--app-fg,#fff)] focus:outline-none focus:border-violet-500 transition-colors"
            />
          </div>
        )}

        {!success && (
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t("common.loading")}
              </>
            ) : mode === "login" ? (
              t("auth.login")
            ) : mode === "signup" ? (
              t("auth.signup")
            ) : (
              t("auth.sendResetLink")
            )}
          </button>
        )}
      </form>

      <p className="mt-4 text-center text-sm text-white/60">
        {mode === "login" ? (
          <>
            {t("auth.noAccount")}{" "}
            <button
              type="button"
              onClick={() => { setMode("signup"); setError(""); setSuccess(""); }}
              className="text-violet-400 hover:text-violet-300 transition-colors"
            >
              {t("auth.signup")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
              className="text-violet-400 hover:text-violet-300 transition-colors"
            >
              {t("auth.backToLogin")}
            </button>
          </>
        )}
      </p>

      {mode === "login" && (
        <p className="mt-3 text-center text-xs text-white/40">
          {t("auth.optional")}
        </p>
      )}
      </>
      )}
    </Modal>
  );
}
